//@ts-strict-ignore
import moment from 'moment';
import { formatServicesDate } from './DateHelper';
import { FilterOperation, ICompany, IFilterInput, Maybe } from '../graphql/schema/p4types';
import { IAvailableColumn } from '../components/view/ColumnSelector';

export enum UIFilterType {
  filterStrings = 'filterStrings',
  filterBooleans = 'filterBooleans',
  filterObjects = 'filterObjects',
  filterEnums = 'filterEnums',
  filterDates = 'filterDates',
  filterDateTimes = 'filterDateTimes',
  filterLongs = 'filterLongs',
  filterBigDecimals = 'filterBigDecimals',
  filterAttributeStrings = 'filterAttributeStrings',
  filterAttributeDates = 'filterAttributeDates',
  filterAttributeObjects = 'filterAttributeObjects',
  filterAttributeBigDecimals = 'filterAttributeBigDecimals',
  filterAttributeLongs = 'filterAttributeLongs',
}

export const FILTER_TYPES = [
  UIFilterType.filterStrings,
  UIFilterType.filterBooleans,
  UIFilterType.filterObjects,
  UIFilterType.filterEnums,
  UIFilterType.filterDates,
  UIFilterType.filterDateTimes,
  UIFilterType.filterLongs,
  UIFilterType.filterBigDecimals,
  UIFilterType.filterAttributeStrings,
  UIFilterType.filterAttributeDates,
  UIFilterType.filterAttributeObjects,
  UIFilterType.filterAttributeBigDecimals,
  UIFilterType.filterAttributeLongs,
];

function getCurrentFinancialQuarter(company: ICompany) {
  const financialPeriodList = company.currentFinancialYear?.financialPeriodList;
  // If the number of financial periods in the year is not 12, we are defaulting
  // to the financial year instead of the financial quarter, as it is ambiguous
  // as to how to represent 'quarter' otherwise
  if (financialPeriodList?.length === 12) {
    const currentFinancialPeriodEnd = company.currentFinancialPeriod?.endDate;
    // Loop through each quarter (3 period interval), comparing the end dates to
    // the current period end date to find the current quarter
    for (let i = 2; i < 12; i += 3) {
      if (moment(currentFinancialPeriodEnd).isSameOrBefore(financialPeriodList[i]?.endDate)) {
        return {
          from: financialPeriodList[i - 2]?.startDate,
          to: financialPeriodList[i]?.endDate,
        };
      }
    }
  }
  console.error(
    'Financial Calendar Invalid',
    "Financial calendar is not compatible with 'quarter', defaulting to 'year'"
  );
  return {
    from: company.currentFinancialYear?.yearStartDate,
    to: company.currentFinancialYear?.yearEndDate,
  };
}

function calculateRelativeDate(filter, company: Maybe<ICompany>) {
  switch (filter.dateRelative) {
    case 'TODAY':
      return moment();
    case 'START_OF_WEEK':
      return moment().startOf('week');
    case 'END_OF_WEEK':
      return moment().endOf('week').add(1, 'days');
    case 'START_OF_MONTH':
      return moment().startOf('month');
    case 'END_OF_MONTH':
      return moment().endOf('month').add(1, 'days');
    case 'START_OF_QUARTER':
      if (filter.financialCalendar && company && company.currentFinancialPeriod && company.currentFinancialYear) {
        return moment(getCurrentFinancialQuarter(company).from, 'YYYY-MM-DD');
      }
      return moment().startOf('quarter');
    case 'END_OF_QUARTER':
      if (filter.financialCalendar && company && company.currentFinancialPeriod && company.currentFinancialYear) {
        return moment(getCurrentFinancialQuarter(company).to, 'YYYY-MM-DD').add(1, 'days');
      }
      return moment().endOf('quarter').add(1, 'days');
    case 'START_OF_YEAR':
      if (filter.financialCalendar && company?.currentFinancialYear) {
        return moment(company.currentFinancialYear.yearStartDate, 'YYYY-MM-DD');
      }
      return moment().startOf('year');
    case 'END_OF_YEAR':
      if (filter.financialCalendar && company?.currentFinancialYear) {
        return moment(company.currentFinancialYear.yearEndDate, 'YYYY-MM-DD').add(1, 'days');
      }
      return moment().endOf('year').add(1, 'days');
    default:
      return moment();
  }
}

function calculateDates(filter, company: Maybe<ICompany>) {
  const relativeDateFrom = calculateRelativeDate(filter, company);
  const relativeDateTo = moment(relativeDateFrom);
  const period = filter.dateType.toLowerCase().substr(0, 4);
  let number = 1;
  let type;
  if (filter.dateType.substr(4, 3) === '_X_') {
    number =
      filter.numberOfPeriods && typeof filter.numberOfPeriods === 'string'
        ? Number.parseFloat(filter.numberOfPeriods)
        : filter.numberOfPeriods
        ? filter.numberOfPeriods
        : 1;
    type = filter.dateType.toLowerCase().substr(7, 7).trim();
  } else {
    type = filter.dateType.toLowerCase().substr(5, 7).trim();
  }
  if (period === 'this') {
    if (
      type === 'quarter' &&
      filter.financialCalendar &&
      company?.currentFinancialPeriod &&
      company?.currentFinancialYear
    ) {
      const currentFinancialQuarter = getCurrentFinancialQuarter(company);
      return {
        from: formatServicesDate(moment(currentFinancialQuarter.from, 'YYYY-MM-DD')),
        to: formatServicesDate(moment(currentFinancialQuarter.to, 'YYYY-MM-DD')),
      };
    } else if (type === 'year' && filter.financialCalendar && company?.currentFinancialYear) {
      return {
        from: formatServicesDate(moment(company.currentFinancialYear.yearStartDate, 'YYYY-MM-DD')),
        to: formatServicesDate(moment(company.currentFinancialYear.yearEndDate, 'YYYY-MM-DD')),
      };
    }
    return {
      from: formatServicesDate(moment().startOf(type)),
      to: formatServicesDate(moment().endOf(type)),
    };
  }
  if (period === 'next') {
    return {
      from: formatServicesDate(relativeDateFrom),
      to: formatServicesDate(relativeDateTo.add(number, type).subtract(1, 'days')),
    };
  }
  if (period === 'last') {
    return {
      from: formatServicesDate(relativeDateFrom.subtract(number, type)),
      to: formatServicesDate(relativeDateTo.subtract(1, 'days')),
    };
  }
}

function getDates(filter, company: Maybe<ICompany>) {
  const { dateType } = filter;
  if (!dateType || typeof dateType !== 'string') return { from: filter.from, to: filter.to };
  if (dateType === 'DATE_RANGE') {
    return {
      from: filter.from,
      to: filter.to,
    };
  }
  if (dateType.startsWith('THIS') || dateType.startsWith('NEXT') || dateType.startsWith('LAST')) {
    return calculateDates(filter, company);
  }
  const today = moment();
  let from, to;
  switch (dateType) {
    case 'TODAY':
      from = today;
      to = today;
      break;
    case 'TOMORROW':
      const tomorrow = moment(today).add(1, 'days');
      from = tomorrow;
      to = tomorrow;
      break;
    case 'YESTERDAY':
      const yesterday = moment(today).subtract(1, 'days');
      from = yesterday;
      to = yesterday;
      break;
  }
  return {
    from: formatServicesDate(from),
    to: formatServicesDate(to),
  };
}

function getNumberFilters(filter) {
  const { filterType } = filter;
  return !filterType || typeof filterType !== 'string' || filterType === 'RANGE'
    ? {
        op: 'GREATER_THAN_EQUAL',
        from: filter.from,
        op2: 'LESS_THAN_EQUAL',
        to: filter.to,
      }
    : {
        op: filterType,
        from: filter.from,
        op2: null,
        to: null,
      };
}

const getFieldList = (filter, availableColumns: Array<IAvailableColumn>) => {
  if (filter.fieldColumn?.idList) {
    return filter.fieldColumn.idList;
  } else {
    // Try to find it against the availableColumns
    const matchingColumn = availableColumns.find(
      (availableColumn: IAvailableColumn) => availableColumn.fieldName === filter.fieldName
    );
    if (matchingColumn?.idList) {
      return matchingColumn.idList;
    }
  }

  return undefined;
};

export const getFilters: (
  view: any,
  predefinedFilters: Array<IFilterInput>,
  company: Maybe<ICompany>,
  availableColumns: Array<IAvailableColumn>
) => Array<IFilterInput> = (view, predefinedFilters, company, availableColumns) => {
  if (!view) return predefinedFilters || [];

  const filters: Array<IFilterInput> = [];
  if (predefinedFilters) {
    filters.push(...predefinedFilters);
  }
  if (view.genericFilter) {
    filters.push({
      filterOperation: FilterOperation.Contains,
      filterParameters: {
        field: 'generic',
        value: view.genericFilter,
      },
    });
  }
  FILTER_TYPES.forEach((filterType) => {
    if (view[filterType]) {
      view[filterType].forEach((filter) => {
        let oper: FilterOperation = FilterOperation.Equals;
        let value = filter.value;
        let oper2, value2;
        let values;
        switch (filterType) {
          case 'filterStrings':
            oper = filter.filterType;
            value = filter.containsText;
            break;
          case 'filterAttributeStrings':
            value = null;
            if (filter.containsText) {
              filters.push(
                toAttributeFilter(filter, [{ key: 'textValue', value: filter.containsText, op: filter.filterType }])
              );
            }
            break;
          case 'filterBooleans':
            if (filter.yes && filter.no) {
              filters.push({
                filterOperation: FilterOperation.Equals,
                filterParameters: {
                  field: filter.fieldName,
                  value: 'false',
                },
              });
            }
            oper = FilterOperation.Equals;
            value = filter.yes;
            break;
          case 'filterEnums':
            oper = FilterOperation.Equals;
            if (Array.isArray(filter.codes)) {
              filter.codes.forEach((code) => {
                filters.push({
                  filterOperation: FilterOperation.Equals,
                  filterParameters: {
                    field: filter.fieldName,
                    value: code,
                  },
                });
              });
            } else {
              value = filter.codes && filter.codes.value;
            }
            break;
          case 'filterAttributeObjects':
            oper = FilterOperation.Equals;
            value = null;
            if (Array.isArray(filter.objects) && filter.objects.length > 0) {
              const extraClauses: Array<{ key: string; value: string; op: FilterOperation }> = [];

              filter.objects.map((object) => {
                if (object?.id) {
                  extraClauses.push({ key: 'referenceValue.id', value: object.id, op: FilterOperation.Equals });
                }
              });

              filters.push(toAttributeFilter(filter, extraClauses));
            }
            break;
          case 'filterObjects':
            oper = FilterOperation.Equals;
            value = null;
            //Objects section is possibly irrelevant - NL advised to leave in just in case.
            if (Array.isArray(filter.objects)) {
              filter.objects.map((object) => {
                if (object.id) {
                  filters.push({
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: `${filter.fieldName}.id`,
                      value: object?.id,
                    },
                  });
                }
              });
            } else if (Array.isArray(filter.codes)) {
              filter.codes.map((code) => {
                if (code) {
                  filters.push({
                    filterOperation: FilterOperation.Equals,
                    filterParameters: {
                      field: `${filter.fieldName}.id`,
                      value: code,
                    },
                  });
                }
              });
            }
            break;
          case 'filterBigDecimals':
            if (filter.notZero) {
              oper = FilterOperation.NotEquals;
              value = 0;
            } else {
              values = getNumberFilters(filter);
              oper = values.op;
              value = values.from;
              oper2 = values.op2;
              value2 = values.to;
            }
            break;
          case 'filterAttributeBigDecimals':
            value = null;
            values = getNumberFilters(filter);

            if (filter.fieldName && filter.filterType && values.from) {
              const extraClauses: Array<{ key: string; value: string; op: FilterOperation }> = [];

              extraClauses.push({
                op: values.op,
                key: 'doubleValue',
                value: values.from,
              });
              if (values.to) {
                extraClauses.push({
                  op: values.op2,
                  key: 'doubleValue',
                  value: values.to,
                });
              }

              filters.push(toAttributeFilter(filter, extraClauses));
            }
            break;
          case 'filterDateTimes':
          case 'filterDates':
            values = getDates(filter, company);
            oper = FilterOperation.GreaterThanEqual;
            value = values.from;
            if (values.to) {
              oper2 = 'LESS_THAN_EQUAL';
              value2 = values.to;
            }
            break;
          case 'filterAttributeDates':
            value = null;
            values = getDates(filter, company);
            if (filter.fieldName && values.from) {
              const extraClauses: Array<{ key: string; value: string; op: FilterOperation }> = [];

              extraClauses.push({
                op: FilterOperation.GreaterThanEqual,
                key: 'dateValue',
                value: values.from,
              });

              if (values.to) {
                extraClauses.push({
                  op: FilterOperation.LessThanEqual,
                  key: 'dateValue',
                  value: values.to,
                });
              }

              filters.push(toAttributeFilter(filter, extraClauses));
            }
            break;
          case 'filterLongs':
            values = getNumberFilters(filter);
            oper = values.op;
            value = values.from;
            oper2 = values.op2;
            value2 = values.to;
            break;
          case 'filterAttributeLongs':
            value = null;
            values = getNumberFilters(filter);
            if (filter.fieldName && filter.filterType && values.from) {
              const extraClauses: Array<{ key: string; value: string; op: FilterOperation }> = [];

              extraClauses.push({
                op: values.op,
                key: 'integerValue',
                value: values.from,
              });
              if (values.to) {
                extraClauses.push({
                  op: values.op2,
                  key: 'integerValue',
                  value: values.to,
                });
              }

              filters.push(toAttributeFilter(filter, extraClauses));
            }
            break;
        }
        if (value && oper === FilterOperation.Equals) {
          const filterToPush: IFilterInput = {
            filterOperation: oper,
            filterParameters: {
              field: filter.fieldName,
              value: value,
            },
          };
          filterToPush.idList = getFieldList(filter, availableColumns);
          filters.push(filterToPush);
        }
        if (value || value === false || value === 0 || oper === FilterOperation.NotEquals) {
          const filterToPush: IFilterInput = {
            filterOperation: oper,
            filterParameters: {
              field: filter.fieldName,
              value: value,
            },
          };
          filterToPush.idList = getFieldList(filter, availableColumns);
          filters.push(filterToPush);
        }
        if (oper2 && (value2 || value2 === false || value2 === 0)) {
          const filterToPush: IFilterInput = {
            filterOperation: oper2,
            filterParameters: {
              field: filter.fieldName,
              value: value2,
            },
          };
          filterToPush.idList = getFieldList(filter, availableColumns);
          filters.push(filterToPush);
        }
      });
    }
  });

  return filters;
};

function toAttributeFilter(filter, extraClauses) {
  const subFilters: Array<IFilterInput> = [];

  subFilters.push(addAttributeFilter(filter));

  extraClauses.forEach(({ key, op, value }) => {
    subFilters.push({
      filterOperation: op,
      filterParameters: {
        field: `${getAttributeFilterField(filter)}.${key}`,
        value: value,
      },
    });
  });

  return {
    filterParameters: {
      filters: subFilters,
    },
  };
}

function getAttributeFilterField(filter) {
  const path = filter.fieldName.split('.');
  path.splice(-3);
  return path.join('.');
}

function addAttributeFilter(filter) {
  const path = filter.fieldName.split('.');
  path.pop();
  const fieldName = path.join('.');

  // Strip prefixes we have added to column names for attribute columns
  // in SalesTransactionWorkspace. Otherwise filtering will not work
  const value = filter.label.replace(/^(Customer - |Delivery Customer - )/, '');

  const attributeFilter: IFilterInput = {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: `${fieldName}`,
      value: value,
    },
  };
  return attributeFilter;
}

export const createFilter = (condition: FilterOperation, field: string, value: string): Array<IFilterInput> => {
  return [
    {
      filterOperation: condition,
      filterParameters: {
        field: field,
        value: value,
      },
    },
  ];
};
